"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@iconify/react";
import { UpgradeModal, type UpgradePlan } from "@/components/UpgradeModal";
import { ConversationCreditsExplainer } from "@/components/ConversationCreditsExplainer";
import { FEATURED_LANDING_SLUGS, getTemplateBySlug } from "@/lib/proxy-templates";
import { COACHING_LANDING_CARD } from "@/lib/coaching";
import { getAuthToken } from "@/lib/client-auth";

export default function HomePage() {
  const router = useRouter();
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<UpgradePlan | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  // Start false so SSR and the first client render match. localStorage is only
  // readable after mount; reading it during render caused hydration mismatches.
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      setIsAuthed(true);
      router.replace("/dashboard");
    }
  }, [router]);

  function templateHref(slug: string) {
    if (isAuthed) return `/dashboard?template=${slug}`;
    return `/register?template=${slug}`;
  }

  function primaryHref() {
    if (isAuthed) return "/dashboard?create=1";
    return "/register";
  }

  async function startCheckout(plan: UpgradePlan) {
    const token = getAuthToken();
    if (!token) {
      router.push(`/login?next=/dashboard&checkoutPlan=${plan}`);
      return;
    }

    setCheckoutLoadingPlan(plan);
    try {
      const response = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json();
      if (!response.ok || !data.checkoutUrl) {
        throw new Error(data.error || "Failed to start checkout");
      }
      window.location.href = data.checkoutUrl as string;
    } catch {
      router.push("/dashboard?billing=checkout_error");
    } finally {
      setCheckoutLoadingPlan(null);
    }
  }

  return (
    <div className="bg-[#FAFAFA] text-gray-900 min-h-screen selection:bg-[#5B8CFF]/20 flex flex-col">
      <UpgradeModal
        open={upgradeModalOpen}
        currentPlan="free"
        loadingPlan={checkoutLoadingPlan}
        onClose={() => setUpgradeModalOpen(false)}
        onSelectPlan={(plan) => {
          void startCheckout(plan);
        }}
      />
      {/* Ambient Background Glow */}
      <div className="pointer-events-none fixed inset-0 flex justify-center overflow-hidden z-[-1]">
        <div className="h-[40rem] w-[80rem] flex-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#5B8CFF]/10 via-[#FAFAFA] to-[#FAFAFA] opacity-50"></div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-gray-200 bg-[#FAFAFA]/80 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-8">
              <Link href="/" className="flex items-center gap-2 group cursor-pointer">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#5B8CFF] to-[#22C55E] text-white shadow-[0_0_15px_rgba(91,140,255,0.3)] transition-transform group-hover:scale-105">
                  <Icon icon="solar:ghost-smile-linear" className="text-xl" />
                </div>
                <span className="text-xl font-semibold tracking-tight text-gray-900">
                  myaiproxy.app
                </span>
              </Link>

              <div className="hidden md:flex space-x-1 text-sm font-medium">
                <a href="#how-it-works" className="rounded-lg px-3 py-2 text-gray-500 hover:text-gray-900 transition-colors">
                  How it Works
                </a>
                <a href="#use-cases" className="rounded-lg px-3 py-2 text-gray-500 hover:text-gray-900 transition-colors">
                  Use Cases
                </a>
                <a href="#pricing" className="rounded-lg px-3 py-2 text-gray-500 hover:text-gray-900 transition-colors">
                  Pricing
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Link href="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 w-full">
        {/* HERO SECTION */}
        <section className="mx-auto max-w-6xl px-4 pt-20 pb-16 sm:px-6 lg:px-8 text-center flex flex-col items-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gradient mb-6 max-w-4xl leading-[1.1]">
            Send your AI instead.
          </h1>
          <p className="text-lg text-gray-500 mb-8 max-w-2xl leading-relaxed">
            Have your AI talk to prospects, candidates, customers, or anyone you need answers from. Send one link. They talk. You get the transcript, answers, and summary.
          </p>

          <p className="text-base text-gray-500 mb-10 max-w-2xl leading-relaxed">
            No scheduling. No app required. No account required for respondents.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link href={primaryHref()} className="inline-flex h-11 items-center justify-center rounded-lg bg-gray-900 px-6 text-sm font-medium text-white transition-all hover:bg-gray-800 shadow-sm">
              Create Your AI Proxy
            </Link>
            <a href="#how-it-works" className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-200 bg-white px-6 text-sm font-medium text-gray-900 transition-all hover:bg-gray-50 shadow-sm">
              See How It Works
            </a>
          </div>
        </section>

        <section id="use-cases" className="border-t border-gray-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FEATURED_LANDING_SLUGS.map((slug) => {
                const template = getTemplateBySlug(slug);
                if (!template) return null;
                return (
                  <Link
                    key={slug}
                    href={templateHref(slug)}
                    className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-6 text-left hover:border-[#5B8CFF]/40 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-gray-200 text-[#5B8CFF]">
                        <Icon icon={template.icon} className="text-xl" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{template.description}</p>
                  </Link>
                );
              })}
              <Link
                href={templateHref(COACHING_LANDING_CARD.templateSlug)}
                className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-6 text-left hover:border-[#5B8CFF]/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-gray-200 text-[#5B8CFF]">
                    <Icon icon="solar:clipboard-check-linear" className="text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{COACHING_LANDING_CARD.title}</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">{COACHING_LANDING_CARD.description}</p>
                <span className="text-sm font-medium text-[#5B8CFF]">{COACHING_LANDING_CARD.cta}</span>
              </Link>
            </div>
          </div>
        </section>

        {/* WHAT PROXY IS */}
        <section className="border-t border-gray-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 mb-4">
              Not a chatbot. Your AI handles the conversation.
            </h2>
            <p className="text-lg text-gray-500 max-w-3xl mx-auto mb-12">
              Create an <strong className="font-medium text-gray-900">AI Proxy</strong>, send one link, and let it ask the questions you need answered. Use a <strong className="font-medium text-gray-900">Group Interview</strong> when you need the same proxy to talk to many people separately and combine their answers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16 text-left">
              <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900 text-white">
                    <Icon icon="solar:ghost-smile-linear" className="text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Proxy</h3>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  One recipient, one conversation. Relay a message, gather requirements,
                  or run a structured check-in. You get a transcript and summary by email.
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#5B8CFF] text-white">
                    <Icon icon="solar:clipboard-list-linear" className="text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Group Interview</h3>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Send the same AI Proxy to multiple people and collect their answers in one place.
                </p>
              </div>
            </div>

            {/* Process Line */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 relative z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FAFAFA] border border-gray-200 text-gray-900 shadow-sm">
                  <Icon icon="solar:chat-round-dots-linear" className="text-2xl" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Conversation
                </span>
              </div>

              <Icon icon="solar:arrow-right-linear" className="hidden sm:block text-2xl text-gray-300" />
              <Icon icon="solar:arrow-down-linear" className="sm:hidden text-2xl text-gray-300" />

              <div className="flex flex-col items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FAFAFA] border border-gray-200 text-[#5B8CFF] shadow-sm">
                  <Icon icon="solar:magnet-linear" className="text-2xl" />
                </div>
                <span className="text-sm font-medium text-gray-900">Capture</span>
              </div>

              <Icon icon="solar:arrow-right-linear" className="hidden sm:block text-2xl text-gray-300" />
              <Icon icon="solar:arrow-down-linear" className="sm:hidden text-2xl text-gray-300" />

              <div className="flex flex-col items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FAFAFA] border border-gray-200 text-[#22C55E] shadow-sm">
                  <Icon icon="solar:layers-linear" className="text-2xl" />
                </div>
                <span className="text-sm font-medium text-gray-900">Structure</span>
              </div>

              <Icon icon="solar:arrow-right-linear" className="hidden sm:block text-2xl text-gray-300" />
              <Icon icon="solar:arrow-down-linear" className="sm:hidden text-2xl text-gray-300" />

              <div className="flex flex-col items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-900 border border-gray-900 text-white shadow-sm">
                  <Icon icon="solar:inbox-in-linear" className="text-2xl" />
                </div>
                <span className="text-sm font-medium text-gray-900">Deliver</span>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 mb-3">
              How it works
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Same voice experience — whether you&apos;re talking to one person or surveying many.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-900">
                1
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                Create
              </h3>
              <ul className="space-y-3 text-sm text-gray-500 mt-2">
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  Create a proxy or paste a survey questionnaire.
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  Set your welcome message and optional context.
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  Choose a voice persona for the AI interviewer.
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  That&apos;s it.
                </li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm relative overflow-hidden">
              <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[#5B8CFF]/5 blur-2xl"></div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-900">
                2
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                Generate Link
              </h3>
              <ul className="space-y-3 text-sm text-gray-500 mt-2 relative z-10">
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  Generate your secure link instantly.
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  Send it to one person (proxy) or your whole audience (survey).
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  They click and talk — voice or text chat.
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:alt-arrow-right-linear" className="mt-0.5" />
                  No app download. No account required.
                </li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm relative overflow-hidden">
              <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[#22C55E]/5 blur-2xl"></div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-sm font-medium text-white shadow-sm">
                3
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                Receive
              </h3>
              <p className="text-sm text-gray-900 font-medium mb-1 relative z-10">
                Get structured results in your dashboard.
              </p>
              <p className="text-sm text-gray-500 mb-4 relative z-10">You receive:</p>
              <ul className="space-y-3 text-sm text-gray-500 relative z-10">
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-read-linear" className="mt-0.5 text-[#22C55E]" />
                  Proxy: email summary + transcript
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-read-linear" className="mt-0.5 text-[#22C55E]" />
                  Survey: collated summary across all respondents
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-read-linear" className="mt-0.5 text-[#22C55E]" />
                  Per-question breakdowns and individual answers
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-read-linear" className="mt-0.5 text-[#22C55E]" />
                  JSON answers captured automatically
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* REAL VOICE OPTION */}
        <section className="border-y border-gray-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-gray-900 mb-6">
                  Real-Time Voice
                </h2>
                <ul className="space-y-4 text-base text-gray-500">
                  <li className="flex items-start gap-3">
                    <Icon icon="solar:microphone-3-linear" className="text-xl text-gray-900 mt-0.5" />
                    Natural voice conversation powered by AI — for proxies and surveys.
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon icon="solar:document-text-linear" className="text-xl text-gray-900 mt-0.5" />
                    Transcript always captured. Survey answers extracted as structured JSON.
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon icon="solar:magic-stick-3-linear" className="text-xl text-gray-900 mt-0.5" />
                    Summaries generated after each session — collated across respondents for surveys.
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-center rounded-3xl border border-gray-100 bg-[#FAFAFA] p-12 shadow-inner h-full min-h-[300px]">
                {/* Voice Orb Animation */}
                <div className="relative flex h-32 w-32 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#5B8CFF] to-[#22C55E] blur-[20px] opacity-40 animate-pulse"></div>
                  <div className="absolute inset-2 rounded-full bg-white shadow-sm"></div>
                  <div className="relative h-16 w-16 rounded-full bg-gradient-to-tr from-[#5B8CFF] to-[#22C55E] shadow-inner flex items-center justify-center">
                    <Icon icon="solar:soundwave-linear" className="text-white text-3xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold tracking-tight text-gray-900 mb-4 text-center">
            Built for focused conversations and voice surveys
          </h2>
          <p className="text-lg text-gray-500 mb-10 text-center max-w-3xl mx-auto">
            Use Proxy or Survey for:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Professional */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                  <Icon icon="solar:case-minimalistic-linear" className="text-xl" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                  Work
                </h3>
              </div>
              <ul className="space-y-4 text-sm text-gray-600">
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Requirement gathering
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Client qualification
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Budget screening
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Applicant Interviews
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Appointment Scheduling
                </li>
                <li className="flex items-center gap-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Structured follow-ups
                </li>
              </ul>
            </div>

            {/* Surveys */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#5B8CFF]/10 text-[#5B8CFF]">
                  <Icon icon="solar:clipboard-list-linear" className="text-xl" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                  Surveys
                </h3>
              </div>
              <ul className="space-y-4 text-sm text-gray-600">
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Customer feedback
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Team pulse checks
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Event or workshop debriefs
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Product research interviews
                </li>
                <li className="flex items-center gap-3">
                  <Icon icon="solar:check-square-linear" className="text-[#5B8CFF] text-lg" />
                  Anonymous voice questionnaires
                </li>
              </ul>
            </div>

            {/* Personal */}
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                  <Icon icon="solar:home-smile-linear" className="text-xl" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                  Personal
                </h3>
              </div>
              <ul className="space-y-4 text-sm text-gray-600">
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#22C55E] text-lg" />
                  Message relay
                </li>
                <li className="flex items-center gap-3 border-b border-gray-50 pb-3">
                  <Icon icon="solar:check-square-linear" className="text-[#22C55E] text-lg" />
                  Structured follow-ups
                </li>
                <li className="flex items-center gap-3">
                  <Icon icon="solar:check-square-linear" className="text-[#22C55E] text-lg" />
                  Focused conversations
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 max-w-5xl mx-auto rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                <Icon icon="solar:clock-circle-linear" className="text-xl" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Each proxy or survey link includes up to 30 minutes of conversation time per respondent.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Surveys stay open for 14 days so you can collect responses from many people on one link.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="border-t border-gray-200 bg-white py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-900 mb-4">
                <Icon icon="solar:shield-check-linear" className="text-2xl" />
              </div>
              <h2 className="text-3xl font-semibold tracking-tight text-gray-900">
                Private by Default
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#FAFAFA] p-4 text-sm font-medium text-gray-900">
                <Icon icon="solar:clock-circle-linear" className="text-gray-400 text-lg" />
                Expiry enforced automatically
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#FAFAFA] p-4 text-sm font-medium text-gray-900">
                <Icon icon="solar:link-circle-linear" className="text-gray-400 text-lg" />
                Signed secure links
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#FAFAFA] p-4 text-sm font-medium text-gray-900">
                <Icon icon="solar:microphone-3-linear" className="text-gray-400 text-lg" />
                Clear audio consent
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#FAFAFA] p-4 text-sm font-medium text-gray-900 md:col-start-1 md:col-end-2 md:ml-auto w-full md:w-auto">
                <Icon icon="solar:document-linear" className="text-gray-400 text-lg" />
                Transcript stored separately
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#FAFAFA] p-4 text-sm font-medium text-gray-900 md:col-start-2 md:col-end-4 w-full md:w-auto">
                <Icon icon="solar:user-block-linear" className="text-gray-400 text-lg" />
                No exposure of your account
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="border-t border-gray-200 bg-[#FAFAFA] py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 mb-4">
              Pricing
            </h2>
            <p className="text-sm text-gray-500 mb-10 max-w-2xl mx-auto">
              Create and share AI Proxies freely. Conversation credits are used when someone starts
              an AI conversation.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 text-left">
              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-medium text-gray-900">Free</h3>
                <div className="my-4 flex items-baseline text-4xl font-semibold tracking-tight text-gray-900">
                  $0
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li>5 conversation credits / month</li>
                  <li>1 active AI Proxy</li>
                  <li>Up to 15 minutes per conversation</li>
                  <li>Transcripts + basic AI summaries</li>
                  <li>Core templates</li>
                </ul>
                <Link href={primaryHref()} className="mt-8 inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50">
                  Create Your AI Proxy
                </Link>
              </div>

              <div className="flex flex-col rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-medium text-gray-900">Starter</h3>
                <div className="my-4 flex items-baseline text-4xl font-semibold tracking-tight text-gray-900">
                  $25
                  <span className="text-sm font-medium text-gray-500 ml-1">/month</span>
                </div>
                <ul className="flex-1 space-y-3 text-sm text-gray-600 mb-8 mt-2">
                  <li>10 conversation credits</li>
                  <li>Unlimited AI Proxies</li>
                  <li>Up to 30-minute conversations</li>
                  <li>Transcripts + AI summaries</li>
                  <li>Group Interviews</li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">
                  Up to 10 × 15-minute conversations or 5 × 30-minute conversations.
                </p>
                <button
                  type="button"
                  onClick={() => setUpgradeModalOpen(true)}
                  disabled={checkoutLoadingPlan !== null}
                  className="inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 shadow-sm disabled:opacity-60"
                >
                  {checkoutLoadingPlan ? "Redirecting..." : "Start with Starter"}
                </button>
              </div>

              <div className="flex flex-col rounded-3xl border-2 border-[#5B8CFF] bg-white p-8 shadow-md relative">
                <div className="absolute -top-3.5 right-8 inline-flex items-center rounded-full bg-[#5B8CFF] px-3 py-1 text-xs font-semibold text-white uppercase tracking-wider">
                  Most Popular
                </div>
                <h3 className="text-lg font-medium text-gray-900">Pro</h3>
                <div className="my-4 flex items-baseline text-4xl font-semibold tracking-tight text-gray-900">
                  $50
                  <span className="text-sm font-medium text-gray-500 ml-1">/month</span>
                </div>
                <ul className="flex-1 space-y-3 text-sm text-gray-600 mb-6 mt-2">
                  <li>30 conversation credits</li>
                  <li>Unlimited AI Proxies</li>
                  <li>Up to 30-minute conversations</li>
                  <li>Transcripts + AI summaries</li>
                  <li>Group Interviews + all templates</li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">
                  Up to 30 × 15-minute conversations or 15 × 30-minute conversations.
                </p>
                <button
                  type="button"
                  onClick={() => setUpgradeModalOpen(true)}
                  disabled={checkoutLoadingPlan !== null}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-3 text-sm font-medium text-white transition-all hover:bg-gray-800 shadow-sm disabled:opacity-60"
                >
                  {checkoutLoadingPlan ? "Redirecting..." : "Upgrade to Pro"}
                </button>
              </div>

              <div className="flex flex-col rounded-3xl border border-emerald-200 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-medium text-gray-900">Recruiting</h3>
                <p className="text-sm text-gray-500 mt-1">For teams screening candidates at volume.</p>
                <div className="my-4 flex items-baseline text-4xl font-semibold tracking-tight text-gray-900">
                  $99
                  <span className="text-sm font-medium text-gray-500 ml-1">/month</span>
                </div>
                <ul className="flex-1 space-y-3 text-sm text-gray-600 mb-6 mt-2">
                  <li>100 conversation credits</li>
                  <li>Unlimited candidate interview Proxies</li>
                  <li>Up to 30-minute candidate interviews</li>
                  <li>Candidate summaries + transcripts</li>
                </ul>
                <p className="text-xs text-gray-500 mb-1">1 credit = up to 15 minutes</p>
                <p className="text-xs text-gray-500 mb-4">
                  Up to 100 × 15-minute interviews or 50 × 30-minute interviews.
                </p>
                <button
                  type="button"
                  onClick={() => void startCheckout("recruiting")}
                  disabled={checkoutLoadingPlan !== null}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-700 px-4 py-3 text-sm font-medium text-white transition-all hover:bg-emerald-800 shadow-sm disabled:opacity-60"
                >
                  {checkoutLoadingPlan === "recruiting" ? "Redirecting..." : "Start Recruiting"}
                </button>
              </div>
            </div>

            <div className="mt-16 max-w-3xl mx-auto text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Interview everyone. Personally interview only the best.
              </h3>
              <p className="text-sm text-gray-600 text-center mb-8">
                Use your AI Proxy for first-round candidate interviews and review summaries before
                deciding who gets a live interview.
              </p>
              <ConversationCreditsExplainer className="rounded-2xl border border-gray-200 bg-white p-6" />
              <div className="mt-8 space-y-4">
                <h4 className="text-lg font-medium text-gray-900">Pricing FAQ</h4>
                <div>
                  <p className="font-medium text-gray-900 text-sm">What is a conversation credit?</p>
                  <p className="text-sm text-gray-600 mt-1">
                    One conversation credit covers up to 15 minutes of AI conversation.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">What if I need a 30-minute interview?</p>
                  <p className="text-sm text-gray-600 mt-1">
                    A Proxy configured for up to 30 minutes uses two conversation credits each time
                    someone starts a conversation.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Does creating a Proxy use credits?</p>
                  <p className="text-sm text-gray-600 mt-1">
                    No. Create and share your Proxies freely. Credits are used when recipients
                    actually start AI conversations. Each proxy includes 1 free Preview; additional
                    Previews use 1 credit each.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    Can I use the same Proxy with multiple candidates?
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Yes. Send the same interview Proxy to as many candidates as you want. Each
                    candidate who starts an AI conversation uses the credits required by that
                    Proxy&apos;s configured duration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-white border-t border-gray-200 py-24 text-center px-4">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            Send your AI instead.
          </h2>
          <p className="text-lg text-gray-500 mb-10">
            Create your first proxy in under 60 seconds.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href={primaryHref()} className="inline-flex h-12 w-full sm:w-auto items-center justify-center rounded-lg bg-gray-900 px-8 text-sm font-medium text-white transition-all hover:bg-gray-800 shadow-md active:scale-95">
              Create Your AI Proxy
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-[#FAFAFA] py-6 text-center text-sm text-gray-400">
        <p>© 2026 myaiproxy.app. All rights reserved.</p>
      </footer>
    </div>
  );
}
